/**
 * Runs panel for API sequence view — shown in sidebar when in API mode.
 *
 * @module FlowDesigner/api/ApiSequenceRunsPanel
 */

import React from 'react';
import { Play } from '@phosphor-icons/react';
import { Button, Text, Flex, Spinner, useToast } from '@chatsdet/ui';
import type { ApiSequenceResult } from '@chatsdet/types';
import {
  useApiSequenceRuns,
  useApiSequenceExecute,
  useApiSequenceUpdateConfig,
  type ApiSequenceDto,
  type ApiSequenceRunDto,
} from './hooks';
import { useApiSequenceDoctor } from './hooks/useApiSequenceDoctor';
import { ApiSequenceResultDrawer } from '../../../components/ApiSequenceResultDrawer';
import { ApiSequenceDoctorLaunchDialog } from '../../../components/ApiSequenceDoctorLaunchDialog';
import type { DoctorState, DoctorMode } from '../../../components/ApiSequenceDoctorView';
import { formatDuration, formatRelativeTime } from '../runs/run-formatters';
import { useWorkflowVariablesContext } from '../context/WorkflowVariablesContext';

type ApiSequenceRunsPanelProps = {
  apiSequenceId: string | null;
  apiSequenceData: ApiSequenceDto | null;
  workflowId: string | null;
  workflowPropertyId: string | null;
  isExecuting: boolean;
  onExecute: () => void;
  lastExecuteResult?: { apiSequenceId: string; runId: string; result: ApiSequenceResult } | null;
};

const statusDotClass: Record<ApiSequenceRunDto['status'], string> = {
  queued: 'bg-muted-foreground/60',
  running: 'bg-warning',
  success: 'bg-success',
  failed: 'bg-destructive',
  canceled: 'bg-muted-foreground/60',
};

const statusLabel: Record<ApiSequenceRunDto['status'], string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Success',
  failed: 'Failed',
  canceled: 'Canceled',
};

const triggerLabel: Record<NonNullable<ApiSequenceRunDto['triggeredVia']>, string> = {
  designer: 'Designer',
  workflow: 'Flow Run',
  seed: 'Seed',
  'test-suite': 'Test Suite',
};

export const ApiSequenceRunsPanel: React.FC<ApiSequenceRunsPanelProps> = ({
  apiSequenceId,
  apiSequenceData,
  workflowId,
  workflowPropertyId,
  isExecuting,
  onExecute,
  lastExecuteResult,
}) => {
  const { toast } = useToast();
  const allWorkflowVars = useWorkflowVariablesContext();
  const [page, setPage] = React.useState(1);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [isApplyingFix, setIsApplyingFix] = React.useState(false);
  const [doctorDialogOpen, setDoctorDialogOpen] = React.useState(false);

  const runsQ = useApiSequenceRuns(apiSequenceId, { page, pageSize: 20 });
  const runs = runsQ.data?.data ?? [];
  const pagination = runsQ.data?.pagination;

  // Doctor hook + mutations for apply-fix flow
  const doctorHook = useApiSequenceDoctor();
  const updateConfigMutation = useApiSequenceUpdateConfig(workflowId);
  const reExecuteMutation = useApiSequenceExecute(apiSequenceId, workflowId);

  // Track the result currently shown in the drawer (may be updated by re-execution)
  const [drawerResultOverride, setDrawerResultOverride] = React.useState<ApiSequenceResult | null>(null);

  // Only honour a result that belongs to THIS sequence — the sidebar's state
  // outlives a flow-to-flow navigation, so an unscoped result would leak
  // flow A's responses (and auto-open the drawer with them) into flow B's panel.
  const ownExecuteResult =
    lastExecuteResult && lastExecuteResult.apiSequenceId === apiSequenceId ? lastExecuteResult : null;

  // Show result drawer for the last execute or a selected run
  const drawerRun = selectedRunId ? (runs.find((r) => r.id === selectedRunId) ?? null) : null;
  const drawerResult = drawerResultOverride ?? drawerRun?.result ?? ownExecuteResult?.result ?? null;

  // Surface a fresh execute immediately — otherwise "Run" only appends a list row
  // and the result looks like nothing happened. Keyed on runId so a re-run reopens
  // the drawer even if it was dismissed, while unrelated re-renders don't. Clearing
  // the selection and override makes drawerResult fall through to the new result.
  // Gated on ownExecuteResult so navigating to a different flow's sequence never
  // auto-opens the drawer with a stale, unscoped result.
  React.useEffect(() => {
    if (!ownExecuteResult?.runId) return;
    setSelectedRunId(null);
    setDrawerResultOverride(null);
    setDrawerOpen(true);
  }, [ownExecuteResult?.runId]);

  const hasFailed = drawerResult?.stepResults.some(
    (s) => !s.success && s.statusCode !== null && s.statusCode !== undefined
  ) ?? false;

  const doctorMode: DoctorMode = hasFailed ? 'fix' : 'improve';

  const handleRunClick = (run: ApiSequenceRunDto) => {
    if (run.result && doctorHook.status === 'idle') {
      setSelectedRunId(run.id);
      setDrawerResultOverride(null);
      setDrawerOpen(true);
    }
  };

  const handleOpenDoctorDialog = React.useCallback(() => {
    setDoctorDialogOpen(true);
  }, []);

  const handleDoctorConfirm = React.useCallback((guidance: string) => {
    if (!apiSequenceData || !drawerResult || !workflowPropertyId) return;
    setDoctorDialogOpen(false);
    doctorHook.doctor({
      propertyId: workflowPropertyId,
      credentialId: apiSequenceData.credentialId,
      currentConfig: apiSequenceData.config,
      failedResult: drawerResult,
      workflowVariables: allWorkflowVars
        .filter(
          (v) => v.source === 'credential' || v.source === 'seed' || v.source === 'childFlowOutput'
        )
        .map((v) => ({ name: v.name, source: v.source, value: v.designTimeValue ?? null })),
      mode: doctorMode,
      userGuidance: guidance || undefined,
    });
  }, [apiSequenceData, drawerResult, doctorHook.doctor, allWorkflowVars, doctorMode, workflowPropertyId]);

  const handleApplyFix = React.useCallback(() => {
    if (!doctorHook.finalConfig || !apiSequenceData) return;
    setIsApplyingFix(true);
    updateConfigMutation.mutate(
      { apiSequenceId: apiSequenceData.id, config: doctorHook.finalConfig },
      {
        onSuccess: () => {
          // Re-execute to verify the fix
          reExecuteMutation.mutate(undefined, {
            onSuccess: (data) => {
              setDrawerResultOverride(data.data.result);
              doctorHook.reset();
              setIsApplyingFix(false);
              toast.success(doctorMode === 'improve' ? 'Improvements applied — sequence re-ran' : 'Fix applied — sequence re-ran');
            },
            onError: (err) => {
              toast.error(`Re-execution failed: ${err.message}`);
              doctorHook.reset();
              setIsApplyingFix(false);
            },
          });
        },
        onError: (err) => {
          toast.error(`Failed to save fix: ${err.message}`);
          setIsApplyingFix(false);
        },
      }
    );
  }, [doctorHook.finalConfig, apiSequenceData, updateConfigMutation, reExecuteMutation, doctorHook.reset, toast, doctorMode]);

  const handleDismissDoctor = React.useCallback(() => {
    doctorHook.reset();
  }, [doctorHook.reset]);

  const handleDrawerOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open && doctorHook.status === 'running') {
        doctorHook.cancel();
      }
      if (!open) {
        doctorHook.reset();
        setDrawerResultOverride(null);
      }
      setDrawerOpen(open);
    },
    [doctorHook.status, doctorHook.cancel, doctorHook.reset]
  );

  const doctorState: DoctorState = {
    status: doctorHook.status,
    mode: doctorMode,
    events: doctorHook.events,
    summary: doctorHook.summary,
    onRunDoctor: handleOpenDoctorDialog,
    onApplyFix: handleApplyFix,
    onCancel: doctorHook.cancel,
    onDismiss: handleDismissDoctor,
    isApplying: isApplyingFix,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Run button */}
      <div className="p-3 border-b border-border">
        <Button
          variant="primary"
          size="sm"
          iconLeft={isExecuting ? <Spinner size="sm" /> : <Play size={16} weight="fill" />}
          onPress={onExecute}
          isDisabled={isExecuting || !apiSequenceId}
        >
          {isExecuting ? 'Running...' : 'Run API Sequence'}
        </Button>
      </div>

      {/* Run history */}
      <div className="flex-1 overflow-y-auto">
        {runsQ.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        )}

        {!runsQ.isLoading && runs.length === 0 && (
          <div className="px-4 py-8 text-center">
            <Text size="sm" className="text-muted-foreground">
              No runs yet. Click "Run" to execute the API sequence.
            </Text>
          </div>
        )}

        {runs.map((run) => {
          const durationMs =
            run.startedAt && run.finishedAt
              ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
              : (run.result?.totalDurationMs ?? null);
          const primaryTimestamp = run.finishedAt ?? run.startedAt ?? run.createdAt;
          const stepCount = run.result?.stepResults.length ?? 0;
          const stepSuccess = run.result?.stepResults.filter((s) => s.success).length ?? 0;
          const stepFailed =
            run.result?.stepResults.filter(
              (s) => !s.success && s.statusCode !== null && s.statusCode !== undefined
            ).length ?? 0;

          return (
            <div
              key={run.id}
              role="button"
              tabIndex={0}
              className="w-full text-left px-3 py-2.5 transition-colors cursor-pointer hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
              onClick={() => handleRunClick(run)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRunClick(run);
                }
              }}
            >
              <Flex justify="between" align="center" gap="sm">
                {/* Left: Status dot + info */}
                <Flex align="center" gap="sm" className="min-w-0 flex-1">
                  <span
                    className={[
                      'h-2 w-2 shrink-0 rounded-full',
                      statusDotClass[run.status],
                      run.status === 'running' ? 'animate-pulse' : '',
                    ].join(' ')}
                    title={statusLabel[run.status]}
                  />
                  <Text size="sm" className="shrink-0">
                    {statusLabel[run.status]}
                  </Text>
                  <Text size="xs" className="text-muted-foreground shrink-0">
                    {formatDuration(durationMs)}
                  </Text>
                  {stepCount > 0 && (
                    <Text size="xs" className="text-muted-foreground truncate">
                      {stepSuccess}/{stepCount} calls
                      {stepFailed > 0 ? ` (${stepFailed} failed)` : ''}
                    </Text>
                  )}
                </Flex>

                {/* Right: Relative time */}
                <Text
                  size="xs"
                  className="text-muted-foreground shrink-0"
                  title={primaryTimestamp ? new Date(primaryTimestamp).toLocaleString() : undefined}
                >
                  {formatRelativeTime(primaryTimestamp)}
                </Text>
              </Flex>

              {(run.triggeredVia && run.triggeredVia !== 'designer') || run.workflowTestRunId ? (
                <div className="mt-1 ml-4 flex items-center gap-2">
                  {run.triggeredVia && run.triggeredVia !== 'designer' && (
                    <span className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {triggerLabel[run.triggeredVia]}
                    </span>
                  )}
                  {run.workflowTestRunId && (
                    <a
                      href={`/runs/tests/${run.workflowTestRunId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-link hover:underline"
                    >
                      View test run →
                    </a>
                  )}
                </div>
              ) : null}

              {run.errorMessage && (
                <Text size="xs" className="text-destructive truncate mt-1 ml-4">
                  {run.errorMessage}
                </Text>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="p-2 border-t border-border flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            isDisabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Text size="xs" className="text-muted-foreground">
            {page} / {pagination.totalPages}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={page >= pagination.totalPages}
            onPress={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Result drawer */}
      {drawerResult && (
        <ApiSequenceResultDrawer
          isOpen={drawerOpen}
          onOpenChange={handleDrawerOpenChange}
          stepResults={drawerResult.stepResults}
          focusIndex={drawerResult.stepResults.findIndex((s) => !s.success)}
          doctor={doctorState}
        />
      )}

      {/* Doctor launch dialog */}
      <ApiSequenceDoctorLaunchDialog
        mode={doctorMode}
        isOpen={doctorDialogOpen}
        onOpenChange={setDoctorDialogOpen}
        onConfirm={handleDoctorConfirm}
      />
    </div>
  );
};
